import React, { useEffect, useMemo, useState } from "react";
import { api, useAuth } from "@/AuthContext";
import { Printer, ArrowUp, ArrowDown, ArrowsLeftRight, ChartLineUp } from "@phosphor-icons/react";

/* ============================================================
 * Tarjeta Informativa — Reporte visual de cumplimiento
 * por autoridad usando tanques de GLP proporcionales al %.
 * Comparativo año actual vs año anterior.
 * ============================================================ */

const AUTH_COLORS = {
  "CNE":       { fill: "#1E40AF", label: "text-blue-700",   soft: "bg-blue-50" },
  "ASEA":      { fill: "#525252", label: "text-neutral-700", soft: "bg-neutral-100" },
  "SENER":     { fill: "#059669", label: "text-emerald-700", soft: "bg-emerald-50" },
  "Energía":   { fill: "#059669", label: "text-emerald-700", soft: "bg-emerald-50" },
  "SEMARNAT":  { fill: "#166534", label: "text-green-700", soft: "bg-green-50" },
  "STPS":      { fill: "#9F1239", label: "text-rose-700",  soft: "bg-rose-50" },
  "IMSS":      { fill: "#7C3AED", label: "text-violet-700", soft: "bg-violet-50" },
  "INFONAVIT": { fill: "#EA580C", label: "text-orange-700", soft: "bg-orange-50" },
  "SAT":       { fill: "#0891B2", label: "text-cyan-700",  soft: "bg-cyan-50" },
  "PROFEPA":   { fill: "#65A30D", label: "text-lime-700",  soft: "bg-lime-50" },
  "Municipal": { fill: "#6B7280", label: "text-neutral-700", soft: "bg-neutral-100" },
};
function colorFor(auth) {
  return AUTH_COLORS[auth] || { fill: "#2563EB", label: "text-blue-700", soft: "bg-blue-50" };
}


export default function TarjetaInformativa() {
  const { user, activeCompanyId, companies, structures, activeStructureId } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/compliance/by-authority", { params: { year } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [year]);

  const companyName = useMemo(
    () => (activeStructureId
      ? (structures || []).find(s => s.id === activeStructureId)?.name
      : (companies || []).find(c => c.id === activeCompanyId)?.name) || "",
    [companies, activeCompanyId, structures, activeStructureId],
  );

  return (
    <div className="space-y-6" data-testid="tarjeta-informativa-page">
      {/* Toolbar - no imprime */}
      <div className="flex items-end justify-between flex-wrap gap-3 print:hidden">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Reportes</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter flex items-center gap-3">
            <ChartLineUp size={36} weight="duotone" className="text-blue-600"/>
            Tarjeta Informativa
          </h1>
          <p className="text-neutral-500 mt-2">Cumplimiento visual por autoridad · {companyName || "Empresa activa"}</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">Año</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || new Date().getFullYear())}
              className="w-28 px-3 py-2 border border-neutral-200 rounded-md text-sm"
              data-testid="tarjeta-year"
            />
          </div>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md"
            data-testid="tarjeta-print"
          >
            <Printer size={16}/> Imprimir / PDF
          </button>
        </div>
      </div>

      {/* Hoja imprimible */}
      <div id="printable-card" className="bg-white border border-neutral-200 rounded-md p-6 max-w-4xl mx-auto print:border-none print:p-0 print:max-w-full">
        {/* Encabezado tipo tarjeta */}
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-neutral-200">
          <div className="text-xl font-black tracking-tight text-neutral-900">Tarjeta Informativa</div>
          <div className="ml-auto text-right">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{activeStructureId ? "Estructura" : "Empresa"}</div>
            <div className="font-semibold text-sm text-neutral-800">{companyName || "—"}</div>
          </div>
        </div>

        <h2 className="text-center font-bold text-white bg-blue-700 rounded-md py-2 mb-6 text-sm tracking-widest uppercase">
          Gráfica de cumplimiento
        </h2>

        {loading && <div className="text-center py-8 text-neutral-400">Cargando...</div>}

        {data && data.items.length === 0 && (
          <div className="text-center py-12 text-neutral-400">
            Sin obligaciones registradas para {companyName || "esta empresa"}.
          </div>
        )}

        {data && data.items.length > 0 && (
          <div className="space-y-8">
            {/* Fila 1: Año actual */}
            <TankRow
              yearLabel={`${data.year}`}
              items={data.items.map(i => ({ ...i, pct: i.current_pct, showArrow: false }))}
            />

            {/* Separador con año */}
            <div className="relative">
              <div className="border-t-2 border-neutral-300"></div>
              <div className="absolute -top-3 left-4 bg-white px-2 text-neutral-500 font-bold text-sm">
                {data.previous_year} →
              </div>
            </div>

            {/* Fila 2: Año anterior (comparativo con flecha de tendencia hacia año actual) */}
            <TankRow
              yearLabel={`${data.previous_year}`}
              items={data.items.map(i => ({
                ...i,
                pct: i.previous_pct,
                showArrow: true,
                arrowDir: i.delta > 0 ? "up" : i.delta < 0 ? "down" : "eq",
              }))}
            />
          </div>
        )}

        {/* Footer print */}
        <div className="mt-8 pt-4 border-t border-neutral-200 text-center text-xs text-neutral-500 print:mt-6">
          Generado por ZAE Documental · {new Date().toLocaleString("es-MX")}
        </div>
      </div>

      {/* Leyenda de arrows */}
      <div className="max-w-4xl mx-auto text-xs text-neutral-500 print:hidden">
        <div className="flex flex-wrap gap-4 justify-center">
          <span className="inline-flex items-center gap-1"><ArrowUp size={12} weight="bold" className="text-emerald-600"/> aumentó respecto a {(data?.previous_year) || "el año anterior"}</span>
          <span className="inline-flex items-center gap-1"><ArrowDown size={12} weight="bold" className="text-rose-600"/> disminuyó</span>
          <span className="inline-flex items-center gap-1"><ArrowsLeftRight size={12} weight="bold" className="text-neutral-500"/> sin cambio</span>
        </div>
      </div>

      {/* Print rules */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          nav, aside, header, .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}


function TankRow({ items, yearLabel }) {
  // Layout responsive: 3 columnas en escritorio
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 place-items-center" data-testid={`tank-row-${yearLabel}`}>
      {items.map(it => (
        <TankCard key={it.authority + yearLabel} item={it} />
      ))}
    </div>
  );
}


function TankCard({ item }) {
  const color = colorFor(item.authority);
  const pct = Math.max(0, Math.min(100, item.pct || 0));
  return (
    <div className="flex flex-col items-center gap-2" data-testid={`tank-${item.authority}-${item.pct}`}>
      {/* Delta arrow arriba del tanque (solo para fila año anterior) */}
      {item.showArrow && (
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${color.label}`}>
          {pct}%{" "}
          {item.arrowDir === "up" && <ArrowUp size={12} weight="bold" className="text-emerald-600"/>}
          {item.arrowDir === "down" && <ArrowDown size={12} weight="bold" className="text-rose-600"/>}
          {item.arrowDir === "eq" && <ArrowsLeftRight size={12} weight="bold" className="text-neutral-500"/>}
        </div>
      )}
      {/* Logo/nombre autoridad arriba (fila superior sin flecha) */}
      {!item.showArrow && (
        <div className={`inline-block px-3 py-1 rounded-md ${color.soft} ${color.label} font-bold text-sm`}>
          {item.authority}
        </div>
      )}
      {/* Tanque SVG */}
      <Tank fillPct={pct} color={color.fill}/>
      {/* Etiqueta debajo */}
      {item.showArrow ? (
        <div className={`inline-block px-3 py-1 rounded-md ${color.soft} ${color.label} font-bold text-sm`}>
          {item.authority}
        </div>
      ) : (
        <div className={`inline-flex items-center justify-center min-w-[64px] px-3 py-1.5 rounded-full ${color.label} font-black text-base`} style={{ background: color.fill, color: 'white' }}>
          {pct}%
        </div>
      )}
      <div className="text-[10px] text-neutral-400">
        {item.satisfied_current !== undefined
          ? `${item.showArrow ? item.satisfied_previous : item.satisfied_current}/${item.obligations_total} obligaciones`
          : ""}
      </div>
    </div>
  );
}


/* Tanque estilizado de GLP - SVG horizontal cylinder */
function Tank({ fillPct, color }) {
  // Definimos el área "interior" del tanque como un rectángulo elíptico
  // fill from bottom: y_start depende del %
  const w = 190;
  const h = 170;
  // Cuerpo circular con línea diagonal (chevron pattern)
  const cx = w / 2, cy = h / 2 - 5, r = 68;
  // Fill height dentro del círculo
  const fillTop = cy + r - (2 * r) * (fillPct / 100);

  const patternId = `chev-${Math.round(Math.random() * 1e6)}`;
  const clipId = `clipTank-${Math.round(Math.random() * 1e6)}`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="drop-shadow-sm">
      <defs>
        <pattern id={patternId} width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="12" stroke="#94a3b8" strokeWidth="0.5" strokeDasharray="2 2"/>
          <line x1="6" y1="0" x2="6" y2="12" stroke="#94a3b8" strokeWidth="0.5" strokeDasharray="2 2"/>
        </pattern>
        <clipPath id={clipId}>
          <circle cx={cx} cy={cy} r={r}/>
        </clipPath>
      </defs>

      {/* Válvula superior */}
      <rect x={cx - 12} y={cy - r - 12} width="24" height="14" rx="2" fill="#1f2937"/>
      <rect x={cx - 16} y={cy - r - 14} width="32" height="4" rx="1" fill="#111827"/>

      {/* Tanque - contorno */}
      <circle cx={cx} cy={cy} r={r} fill="white" stroke="#1f2937" strokeWidth="2.5"/>

      {/* Patrón de chevrones dentro (siempre visible) */}
      <g clipPath={`url(#${clipId})`}>
        <rect x={cx - r} y={cy - r} width={r * 2} height={r * 2} fill={`url(#${patternId})`}/>
      </g>

      {/* Nivel de llenado (fondo del tanque) */}
      {fillPct > 0 && (
        <g clipPath={`url(#${clipId})`}>
          <rect x={cx - r} y={fillTop} width={r * 2} height={cy + r - fillTop} fill={color} opacity="0.95"/>
          {/* Superficie del líquido con leve reflejo */}
          <rect x={cx - r} y={fillTop} width={r * 2} height="2" fill="white" opacity="0.5"/>
        </g>
      )}

      {/* Base con patas */}
      <rect x={cx - r - 6} y={cy + r - 8} width={r * 2 + 12} height="14" rx="2" fill="#1f2937"/>
      <rect x={cx - r} y={cy + r + 6} width="12" height="26" fill="#1f2937"/>
      <rect x={cx + r - 12} y={cy + r + 6} width="12" height="26" fill="#1f2937"/>
    </svg>
  );
}
